"use client";

import { useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      id: 1,
      question: "How long does it take to develop a mobile app?",
      answer:
        "The development timeline varies depending on the complexity and scope of your project. A simple app can take 1-3 months, while more complex applications may require 3-8 months or longer. We'll provide a detailed timeline during our initial consultation after understanding your requirements.",
    },
    {
      id: 2,
      question: "Do you develop for both iOS and Android?",
      answer:
        "Yes! We specialize in both iOS and Android development. We can create native apps for each platform or use cross-platform frameworks like React Native or Flutter to build apps that work on both platforms simultaneously, which can be more cost-effective and faster to deploy.",
    },
    {
      id: 3,
      question: "What is included in your app development process?",
      answer:
        "Our comprehensive process includes: initial consultation and requirements gathering, UI/UX design, development and coding, quality assurance and testing, app store submission, and post-launch support. We maintain transparent communication throughout the entire project lifecycle.",
    },
    {
      id: 4,
      question: "How much does it cost to develop a mobile app?",
      answer:
        "App development costs vary significantly based on features, complexity, design requirements, and platform. We offer custom quotes tailored to your specific needs. During our consultation, we'll discuss your budget and provide a detailed breakdown of costs. We also offer flexible payment plans to accommodate different budgets.",
    },
    {
      id: 5,
      question: "Do you provide ongoing maintenance and support?",
      answer:
        "Absolutely! We offer comprehensive maintenance and support packages to ensure your app continues to perform optimally. This includes bug fixes, security updates, feature enhancements, compatibility updates for new OS versions, and performance monitoring. We're here for the long term.",
    },
    {
      id: 6,
      question: "Will I own the source code and app?",
      answer:
        "Yes, once the project is completed and fully paid, you own all rights to the source code, design assets, and the application itself. We provide you with all source code and documentation, ensuring you have full control and ownership of your investment.",
    },
    {
      id: 7,
      question: "Can you help with app store submission?",
      answer:
        "Yes, we handle the entire app store submission process for both Apple App Store and Google Play Store. This includes preparing all required assets, writing descriptions, handling the submission process, and managing any required revisions. We'll guide you through every step.",
    },
    {
      id: 8,
      question: "What technologies do you use for app development?",
      answer:
        "We use a wide range of modern technologies including React Native, Flutter, Swift (iOS), Kotlin (Android), Node.js for backend services, and various cloud platforms like AWS and Firebase. We choose the best technology stack based on your project's specific requirements and goals.",
    },
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-6">
            <HelpCircle className="w-8 h-8 text-primary-600" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-secondary-900 mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            Have questions? We've got answers. Here are some common questions
            about our mobile app development services.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={faq.id}
              className="bg-white border border-secondary-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 animate-slide-up"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset"
                aria-expanded={openIndex === index}
              >
                <span className="text-lg font-semibold text-secondary-900 pr-4">
                  {faq.question}
                </span>
                <ChevronDown
                  className={`w-5 h-5 text-primary-600 flex-shrink-0 transition-transform duration-200 ${
                    openIndex === index ? "transform rotate-180" : ""
                  }`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  openIndex === index ? "max-h-96" : "max-h-0"
                }`}
              >
                <div className="px-6 pb-5">
                  <p className="text-secondary-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-12 text-center">
          <p className="text-secondary-600 mb-6">
            Still have questions? We're here to help!
          </p>
          <a
            href="#contact"
            className="btn-primary inline-flex items-center space-x-2"
          >
            <span>Get in Touch</span>
          </a>
        </div>
      </div>
    </section>
  );
}
